import { addToast } from '@medly-components/core';
import { TIMER } from 'constants/onboarding';
import { format, isToday } from 'date-fns';
import { RefObject, useEffect } from 'react';
import { Direction, Position } from 'types';

/**
 * Masks the username part of an email address with asterisks ('*') while keeping the domain unchanged.
 * If the input email is an empty string or null, an empty string will be returned.
 *
 * @param {string} email - The email address to be masked.
 * @returns {string} The masked version of the input email address.
 *
 * @example
 * // Returns "*********@gmail.com"
 * maskEmail("john.doe@gmail.com");
 *
 * // Returns ""
 * maskEmail("");
 */
export const maskEmail = (email: string) => {
    if (!email) {
        return '';
    }
    const [username, domain] = email.split('@');
    return `${username.replace(/./g, '*')}@${domain}`;
};

export const useOutsideAlerter = (
    ref: RefObject<HTMLElement>,
    ousideClickAction: () => void,
    { exceptionEleId, capture }: { exceptionEleId?: string; capture?: boolean } = {}
) => {
    useEffect(() => {
        /**
         * Do action if clicked on outside of element
         */
        const handleClickOutside = (event: any) => {
            if (exceptionEleId) {
                const ele = document.getElementById(exceptionEleId);
                if (ele && ele.contains(event.target)) return;
            }
            if (ref.current && !ref.current.contains(event.target)) {
                ousideClickAction();
            }
        };
        // Bind the event listener
        document.addEventListener('click', handleClickOutside, { capture });
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('click', handleClickOutside);
        };
    }, [ref]);
};

export const errorToast = (error: string) => {
    if (error)
        addToast({
            timer: TIMER,
            variant: 'error',
            message: error
        });
};

export const formatDate = (data: string) => {
    if (data) {
        const date = new Date(data);
        if (isToday(date)) return 'Today, ' + format(date, 'hh:mm aaa');
        return format(date, 'MMM dd, hh:mm aaa');
    }
    return '';
};

export const shortHand = (name: string) => {
    return name
        .trim()
        .split(' ')
        .filter(Boolean)
        .map((l: string) => l[0].toUpperCase())
        .join('')
        .slice(0, 2);
};

export const usePreventZoom = () => {
    useEffect(() => {
        const handleZoom = (event: WheelEvent) => {
            if (event.ctrlKey || event.metaKey) {
                event.preventDefault();
            }
        };

        document.addEventListener('wheel', handleZoom, { passive: false });

        return () => {
            document.removeEventListener('wheel', handleZoom);
        };
    }, []);
};

export const isEmptyObject = (obj: Record<string, any>, ignoreKeys: string[] = []): boolean => {
    return Object.entries(obj).every(([key, value]) => {
        if (ignoreKeys.includes(key)) return true;
        if (value && typeof value === 'object') return isEmptyObject(value);
        if (Array.isArray(value)) return value.length === 0;
        return !value;
    });
};

export const filterObject = (obj: Record<string, any>, filterKeys: string[] = []): Record<string, any> =>
    Object.fromEntries(Object.entries(obj).filter(([key]) => !filterKeys.includes(key)));

export const throttle = (func: (...args: any) => void, interval: number) => {
    let isRunning = false;
    return function executedFunction(...args: any[]) {
        if (!isRunning) {
            isRunning = true;
            func(...args);
            setTimeout(() => {
                isRunning = false;
            }, interval);
        }
    };
};

export const removeEmptyString = <T extends Record<string, any>>(obj: Record<string, any>) => {
    return Object.fromEntries(Object.entries(obj).filter(([_, value]) => value !== '')) as T;
};

export const getMovementDirection = (prevPosition: Position, currentPosition: Position): Direction => {
    if (currentPosition.y > prevPosition.y) {
        return Direction.DOWN;
    } else if (currentPosition.y < prevPosition.y) {
        return Direction.UP;
    }
    if (currentPosition.x > prevPosition.x) {
        return Direction.RIGHT;
    } else if (currentPosition.x < prevPosition.x) {
        return Direction.LEFT;
    }
    return Direction.NONE;
};
